import { FileIcon } from "lucide-react"
import type { Media } from "@/shared/lib/types"
import { formatMediaDuration, getMediaTypeName } from "./post-media-viewer.utils"

interface PostMediaItemProps {
	item: Media
	index: number
	compact: boolean
	isPrimaryVideo?: boolean
	minHeight?: number
	videoRef?: React.RefObject<HTMLVideoElement | null>
	onVideoPause?: () => void
	onVideoSeeking?: () => void
	onVideoLoadedMetadata?: (e: React.SyntheticEvent<HTMLVideoElement>) => void
}

export function PostMediaItem({
	item,
	index,
	compact,
	isPrimaryVideo = false,
	minHeight,
	videoRef,
	onVideoPause,
	onVideoSeeking,
	onVideoLoadedMetadata,
}: PostMediaItemProps) {
	switch (item.type) {
		case "video":
			return (
				<div className="group relative overflow-hidden rounded-2xl bg-black">
					<video
						ref={isPrimaryVideo ? videoRef : null}
						src={item.url}
						poster={item.thumbnailUrl || undefined}
						controls
						controlsList="nodownload noremoteplayback"
						disablePictureInPicture
						className={`h-auto w-full object-contain ${
							compact ? "max-h-[300px]" : "max-h-[600px]"
						}`}
						preload="metadata"
						onContextMenu={(e) => e.preventDefault()}
						style={{
							userSelect: "none",
							aspectRatio: item.width && item.height ? `${item.width} / ${item.height}` : undefined,
							minHeight,
						}}
						onLoadedMetadata={onVideoLoadedMetadata}
						onPause={isPrimaryVideo ? onVideoPause : undefined}
						onSeeking={isPrimaryVideo ? onVideoSeeking : undefined}
					>
						Ваш браузер не поддерживает воспроизведение видео.
					</video>
					{item.duration ? (
						<div className="absolute top-2 right-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
							{formatMediaDuration(item.duration)}
						</div>
					) : null}
				</div>
			)

		case "audio":
		case "voice":
			return (
				<div className="rounded-2xl bg-muted p-4">
					<div className="mb-2 flex items-center gap-3">
						<FileIcon className="h-6 w-6 flex-shrink-0 text-muted-foreground" />
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-medium">
								{item.filename || `${getMediaTypeName(item.type)} #${index + 1}`}
							</p>
							{item.size ? (
								<p className="text-xs text-muted-foreground">
									{(item.size / 1024 / 1024).toFixed(2)} МБ
									{item.duration ? ` • ${formatMediaDuration(item.duration)}` : ""}
								</p>
							) : null}
						</div>
					</div>
					<audio
						src={item.url}
						controls
						controlsList="nodownload noremoteplayback"
						className="w-full"
						preload="metadata"
						onContextMenu={(e) => e.preventDefault()}
						style={{ userSelect: "none" }}
					>
						Ваш браузер не поддерживает воспроизведение аудио.
					</audio>
				</div>
			)

		case "video_note":
			return (
				<div className="flex justify-center">
					<div className="relative h-64 w-64 overflow-hidden rounded-full bg-black">
						<video
							src={item.url}
							poster={item.thumbnailUrl || undefined}
							controls
							controlsList="nodownload"
							className="h-full w-full object-cover"
							preload="metadata"
							onContextMenu={(e) => e.preventDefault()}
							style={{ userSelect: "none" }}
						>
							Ваш браузер не поддерживает воспроизведение видео.
						</video>
					</div>
				</div>
			)

		case "document":
			return (
				<div className="flex items-center gap-3 rounded-2xl bg-muted p-4">
					<FileIcon className="h-8 w-8 flex-shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-medium">{item.filename || `Файл #${index + 1}`}</p>
						{item.size ? (
							<p className="text-xs text-muted-foreground">
								{(item.size / 1024 / 1024).toFixed(2)} МБ
							</p>
						) : null}
					</div>
				</div>
			)

		default:
			return null
	}
}
